'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { motion, useInView } from 'framer-motion';
import {
  FileText,
  Users,
  Tags,
  CalendarClock,
  ArrowRight,
  ChevronDown,
  Play,
  UserPlus,
  Link2,
  Download,
} from 'lucide-react';

import { useAuth } from '@/hooks/use-auth';
import { Logo } from '@/components/layout/logo';
import { ThemeToggle } from '@/components/layout/theme-toggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Static imports — lightweight components
import { HeroHighlight, Highlight } from '@/components/landing/hero-highlight';
import { MovingBorderButton } from '@/components/landing/moving-border-button';
import { Meteors } from '@/components/landing/meteors';

// Dynamic imports — heavy animation components (ssr: false for performance)
const Sparkles = dynamic(
  () => import('@/components/landing/sparkles').then(m => ({ default: m.Sparkles })),
  { ssr: false }
);
const LampContainer = dynamic(
  () => import('@/components/landing/lamp-container').then(m => ({ default: m.LampContainer })),
  {
    ssr: false,
    loading: () => (
      <div className="relative flex min-h-[500px] w-full flex-col items-center justify-center overflow-hidden rounded-md bg-muted/30" />
    ),
  }
);
const BackgroundBeams = dynamic(
  () => import('@/components/landing/background-beams').then(m => ({ default: m.BackgroundBeams })),
  { ssr: false }
);
const Marquee3D = dynamic(
  () => import('@/components/landing/marquee-3d').then(m => ({ default: m.Marquee3D })),
  {
    ssr: false,
    loading: () => (
      <div className="h-[400px] sm:h-[480px] lg:h-[560px] rounded-xl bg-muted/10" />
    ),
  }
);
const AnimatedCounter = dynamic(
  () => import('@/components/landing/animated-counter').then(m => ({ default: m.AnimatedCounter })),
  {
    ssr: false,
    loading: () => <span>0</span>,
  }
);
const TypewriterEffect = dynamic(
  () => import('@/components/landing/typewriter-effect').then(m => ({ default: m.TypewriterEffect })),
  {
    ssr: false,
    loading: () => <span className="text-primary font-semibold">XMLs do Período</span>,
  }
);
const TextGenerateEffect = dynamic(
  () => import('@/components/landing/text-generate-effect').then(m => ({ default: m.TextGenerateEffect })),
  {
    ssr: false,
    loading: () => (
      <p className="text-lg font-normal leading-relaxed text-muted-foreground sm:text-xl">
        Gerencie documentos fiscais de todos os seus clientes em um só lugar. XMLs, SPED Fiscal, SPED Contribuições e muito mais.
      </p>
    ),
  }
);

const YOUTUBE_VIDEO_ID = 'ZYM6kCu5K_s';

const typewriterWords = [
  'XMLs do Período',
  'SPED Fiscal',
  'SPED Contribuições',
  'Notas Emitidas',
  'Classificação Tributária',
];

const howItWorksSteps = [
  {
    icon: UserPlus,
    title: 'Cadastre-se',
    description: 'Crie sua conta gratuita em menos de 2 minutos',
  },
  {
    icon: Link2,
    title: 'Vincule Clientes',
    description: 'Conecte as empresas que você atende pelo CNPJ',
  },
  {
    icon: Download,
    title: 'Receba Documentos',
    description: 'XMLs, SPEDs e relatórios gerados automaticamente',
  },
];

const features = [
  {
    icon: FileText,
    title: 'Documentos Fiscais',
    description:
      'XMLs, SPED Fiscal, SPED Contribuições e muito mais em poucos cliques. Baixe tudo que precisa de forma rápida e organizada.',
  },
  {
    icon: Users,
    title: 'Múltiplos Clientes',
    description:
      'Gerencie todos os seus clientes em uma única plataforma. Acesso centralizado a documentos de todas as empresas.',
  },
  {
    icon: Tags,
    title: 'Classificação Tributária',
    description:
      'Classifique produtos diretamente pelo portal. Agilize o processo de classificação fiscal com ferramentas intuitivas.',
  },
  {
    icon: CalendarClock,
    title: 'Recorrência Automática',
    description:
      'Configure a geração automática mensal de documentos. Nunca mais esqueça de solicitar os arquivos do período.',
  },
];

const stats = [
  { value: 1000, suffix: '+', label: 'Contadores Ativos' },
  { value: 50000, suffix: '+', label: 'Documentos Processados' },
  { value: 99, suffix: '.9%', label: 'Disponibilidade' },
  { value: 24, suffix: '/7', label: 'Suporte' },
];

/* ------------------------------------------------------------------ */

function StaggerContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.15 } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

/* ================================================================== */

export default function Home() {
  const router = useRouter();
  const { isAuthenticated, isLoading } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace('/relatorios');
    }
  }, [isAuthenticated, isLoading, router]);

  if (isLoading || isAuthenticated || !mounted) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/60 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link href="/" className="flex items-center gap-2">
            <Logo width={130} height={30} priority />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link href="/login">
              <Button variant="outline" size="sm">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <Sparkles
          particleCount={30}
          particleColor="var(--color-primary, #6366f1)"
          className="z-0"
        />

        <HeroHighlight
          containerClassName="flex flex-col items-center justify-center px-4 pb-20 pt-24 sm:px-6 sm:pt-32 lg:pt-40"
          className="mx-auto max-w-3xl text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
            </span>
            Portal do Contador
          </motion.div>

          {/* Titulo */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Portal do Contador{' '}
            <Highlight>NossoERP</Highlight>
          </motion.h1>

          {/* Subtitulo */}
          <div className="mx-auto mt-6 max-w-2xl">
            <TextGenerateEffect
              words="Gerencie documentos fiscais de todos os seus clientes em um só lugar. XMLs, SPED Fiscal, SPED Contribuições e muito mais."
              className="text-lg font-normal leading-relaxed text-muted-foreground sm:text-xl"
            />
          </div>

          {/* Typewriter */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="mt-6 flex items-center justify-center gap-2 text-lg sm:text-xl"
          >
            <span className="text-muted-foreground">Acesse:</span>
            <TypewriterEffect words={typewriterWords} className="text-primary font-semibold" />
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link href="/login">
              <MovingBorderButton>
                Começar Agora
                <ArrowRight className="h-4 w-4" />
              </MovingBorderButton>
            </Link>
            <a href="#features">
              <Button variant="outline" size="lg" className="gap-2 px-6 text-base">
                Saiba Mais
                <ChevronDown className="h-4 w-4" />
              </Button>
            </a>
          </motion.div>
        </HeroHighlight>
      </section>

      {/* ── Como Funciona ───────────────────────────────────────── */}
      <section className="border-t border-border/40 px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Como Funciona
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Três passos simples para começar
            </p>
          </motion.div>

          <StaggerContainer className="relative mt-14 grid gap-8 sm:grid-cols-3">
            {/* Connector line (desktop) */}
            <div className="pointer-events-none absolute left-0 right-0 top-12 z-0 hidden sm:block">
              <div className="mx-auto h-0.5 w-2/3 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            </div>

            {howItWorksSteps.map((step, i) => (
              <motion.div
                key={step.title}
                variants={cardVariants}
                className="relative z-10 flex flex-col items-center text-center"
              >
                {/* Numbered circle */}
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {i + 1}
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                  <step.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────── */}
      <section
        id="features"
        className="scroll-mt-16 border-t border-border/40 bg-muted/20 px-4 py-20 sm:px-6"
      >
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Funcionalidades pensadas para simplificar o dia a dia do contador
            </p>
          </motion.div>

          <StaggerContainer className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={cardVariants}
                whileHover={{
                  scale: 1.02,
                  borderColor: 'hsl(var(--primary) / 0.4)',
                  boxShadow: '0 10px 25px -5px hsl(var(--primary) / 0.1)',
                }}
                className="group relative overflow-hidden rounded-xl border border-border/60 bg-card p-6 transition-all"
              >
                <Meteors number={10} />
                <div className="relative z-10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/15">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── Screenshots 3D ────────────────────────────────────── */}
      <section className="border-t border-border/40 px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Conheça a Interface do Portal
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Uma plataforma moderna, intuitiva e pensada para o dia a dia do contador
            </p>
          </div>

          <Marquee3D
            className="h-[400px] sm:h-[480px] lg:h-[560px]"
            speed="slow"
            images={[
              [
                '/img/screenshots/relatorios.png',
                '/img/screenshots/clientes.png',
                '/img/screenshots/classificacao.png',
                '/img/screenshots/relatorios.png',
                '/img/screenshots/pesquisa.png',
              ],
              [
                '/img/screenshots/pesquisa.png',
                '/img/screenshots/login.png',
                '/img/screenshots/landing.png',
                '/img/screenshots/clientes.png',
                '/img/screenshots/classificacao.png',
              ],
              [
                '/img/screenshots/login.png',
                '/img/screenshots/recorrencias.png',
                '/img/screenshots/relatorios.png',
                '/img/screenshots/landing.png',
                '/img/screenshots/recorrencias.png',
              ],
            ]}
          />
        </div>
      </section>

      {/* ── Video ───────────────────────────────────────────────── */}
      <section className="border-t border-border/40">
        <LampContainer>
          <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
            Veja o Portal em Ação
          </h2>
          <p className="mt-4 text-center text-lg text-muted-foreground">
            Descubra como o Portal do Contador pode transformar sua rotina
          </p>

          <div className="mt-25 w-full max-w-4xl overflow-hidden rounded-xl border border-border/60 bg-card shadow-xl">
            <div className="relative aspect-video w-full">
              {YOUTUBE_VIDEO_ID ? (
                <iframe
                  className="absolute inset-0 h-full w-full border-0"
                  src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}`}
                  title="Portal do Contador NossoERP"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/50">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Play className="h-8 w-8 text-primary" />
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Vídeo em breve
                  </p>
                </div>
              )}
            </div>
          </div>
        </LampContainer>
      </section>

      {/* ── Estatisticas ────────────────────────────────────────── */}
      <section className="border-t border-border/40 bg-gradient-to-b from-muted/40 to-background px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Números que falam por si
            </h2>
          </motion.div>

          <div className="mt-14 grid grid-cols-2 gap-8 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Final ───────────────────────────────────────────── */}
      <section className="relative border-t border-border/40 px-4 py-20 sm:px-6">
        <BackgroundBeams />
        <div className="relative z-10 mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Pronto para simplificar sua rotina contábil?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Acesse o portal e tenha todos os documentos fiscais dos seus clientes ao
              alcance de um clique.
            </p>
          </motion.div>

          <div className="mt-10 flex flex-col items-center gap-4">
            <Link href="/login">
              <MovingBorderButton>
                Criar Conta Grátis
                <ArrowRight className="h-4 w-4" />
              </MovingBorderButton>
            </Link>
            <Link
              href="/login"
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Já tem conta?{' '}
              <span className="font-medium text-primary underline underline-offset-4">
                Entrar
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="border-t border-border/40 px-4 py-12 sm:px-6">
        <div className="mx-auto grid max-w-6xl gap-8 sm:grid-cols-3">
          {/* Col 1 - Logo */}
          <div>
            <Logo width={120} height={28} />
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Portal do Contador NossoERP — a forma mais simples de gerenciar documentos
              fiscais dos seus clientes.
            </p>
          </div>

          {/* Col 2 - Links */}
          <div>
            <h3 className="text-sm font-semibold">Links</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link
                  href="/login"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  Entrar
                </Link>
              </li>
              <li>
                <Link
                  href="/login"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  Cadastrar
                </Link>
              </li>
              <li>
                <a
                  href="#features"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  Sobre
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3 - Contato */}
          <div>
            <h3 className="text-sm font-semibold">Contato</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href="https://nossoerp.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  nossoerp.com.br
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-6xl border-t border-border/40 pt-6 text-center text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} NossoERP — Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
}
